import cn from 'classnames'
import type {ReactNode, Ref} from 'react'
import {useCallback, useEffect, useRef, useState} from 'react'
import {HOURS_PER_WORK_DAY, MONTHS_PER_YEAR, VOLUME_KEYS} from 'slices/RoiCalculator/constants'
import type {CurrencyCode, ResultsLabels, Savings, VolumeKey} from 'slices/RoiCalculator/types'
import {
  barWidths,
  formatCompactCurrency,
  formatCurrency,
  formatDays,
  MOBILE_COMPACT_THRESHOLD,
} from 'slices/RoiCalculator/utils'

const TRANSITION_MS = 700

export const useAnimatedNumber = (target: number): number => {
  const [display, setDisplay] = useState(target)
  const rafRef = useRef<number>(0)
  const startRef = useRef({value: target, time: 0})

  const animate = useCallback(
    (startValue: number) => {
      startRef.current = {value: startValue, time: performance.now()}

      const tick = (now: number) => {
        const elapsed = now - startRef.current.time
        const progress = Math.min(elapsed / TRANSITION_MS, 1)
        const eased = progress < 0.5 ? 2 * progress * progress : 1 - (-2 * progress + 2) ** 2 / 2
        const current = Math.round(startRef.current.value + (target - startRef.current.value) * eased)

        setDisplay(current)

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(tick)
        }
      }

      cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(tick)
    },
    [target],
  )

  // Animate when target changes
  useEffect(() => {
    animate(display)

    return () => cancelAnimationFrame(rafRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when target changes, display is read not tracked
  }, [target, animate])

  return display
}

const AnimatedHours = ({hours, formatHours}: {hours: number; formatHours: (h: number) => string}) => {
  const animated = useAnimatedNumber(hours)

  return <span className="text-right font-semibold">{formatHours(animated)}</span>
}

const BAR_CLASS_NAMES: Record<VolumeKey, string> = {
  cards: 'bg-ui-blue-120',
  invoices: 'bg-yellow-150',
  reimbursements: 'bg-purple-150',
}

export type PeriodToggleProps = {
  isYearly: boolean
  monthlyLabel: string
  yearlyLabel: string
  onChange: (isYearly: boolean) => void
}

const periodButtonClassName = (isActive: boolean) =>
  cn(
    'rounded-full px-3 py-1 transition-colors',
    isActive ? 'bg-moss-white text-green-dark' : 'text-moss-white/80 hover:bg-moss-white/15',
  )

export const PeriodToggle = ({isYearly, monthlyLabel, yearlyLabel, onChange}: PeriodToggleProps) => (
  <div className="flex shrink-0 items-center rounded-full border border-moss-white/20 p-0.5 text-12">
    <button
      type="button"
      aria-pressed={!isYearly}
      onClick={() => onChange(false)}
      className={periodButtonClassName(!isYearly)}
    >
      {monthlyLabel}
    </button>
    <button
      type="button"
      aria-pressed={isYearly}
      onClick={() => onChange(true)}
      className={periodButtonClassName(isYearly)}
    >
      {yearlyLabel}
    </button>
  </div>
)

export type ResultsCardLayout = 'card' | 'drawer'

export type ResultsCardProps = {
  savings: Savings
  locale: string
  currency: CurrencyCode
  hoursLabel: string
  formatHours: (hours: number) => string
  moneyLabel: string
  breakdownLabels: Record<VolumeKey, string>
  resultsLabels: ResultsLabels
  hourlyRate: number
  isYearly: boolean
  onPeriodChange: (isYearly: boolean) => void
  layout?: ResultsCardLayout
  headlineRef?: Ref<HTMLParagraphElement>
  children?: ReactNode
}

export const ResultsCard = ({
  savings,
  locale,
  currency,
  hoursLabel,
  formatHours,
  moneyLabel,
  breakdownLabels,
  resultsLabels,
  hourlyRate,
  isYearly,
  onPeriodChange,
  layout = 'card',
  headlineRef,
  children,
}: ResultsCardProps) => {
  const isDrawer = layout === 'drawer'
  const widths = barWidths(savings.hours)

  const multiplier = isYearly ? MONTHS_PER_YEAR : 1
  const totalHours = savings.totalHours * multiplier
  const totalMoney = savings.money * multiplier
  const totalDays = totalHours / HOURS_PER_WORK_DAY

  const animatedHours = useAnimatedNumber(totalHours)
  const animatedMoney = useAnimatedNumber(totalMoney)
  const animatedDaysTenths = useAnimatedNumber(Math.round(totalDays * 10))
  const displayDays = formatDays(animatedDaysTenths / 10)

  return (
    <div className="flex flex-col overflow-hidden rounded-lg bg-moss-white">
      {!isDrawer && (
        <div className="flex items-center justify-between gap-3 rounded-t-lg bg-green-dark px-5 py-4 md:px-8">
          <div className="flex items-center gap-2.5">
            <img src="/icons/moss-icon-white.svg" alt="" className="h-5 w-auto" />
            <p className="text-12 font-semibold uppercase tracking-wide text-moss-white md:text-14">
              {resultsLabels.heading}
            </p>
          </div>
          <PeriodToggle
            isYearly={isYearly}
            monthlyLabel={resultsLabels.monthly}
            yearlyLabel={resultsLabels.yearly}
            onChange={onPeriodChange}
          />
        </div>
      )}
      <div className={cn('flex flex-col gap-6', isDrawer ? 'p-5' : 'p-5 md:p-8')}>
        {!isDrawer && (
          <div role="status" className="grid grid-cols-2 gap-4 md:gap-8">
            <div className="flex min-w-0 flex-col gap-3 md:gap-4">
              <p className="min-h-[2.5em] text-14 leading-tight text-moss-grey-550 md:min-h-0 md:text-16 md:leading-none">
                {hoursLabel}
              </p>
              <p ref={headlineRef} className="text-[40px] font-medium leading-none text-moss-black md:text-[64px]">
                {displayDays} <span className="text-[20px] md:text-[32px]">{resultsLabels.days}</span>
              </p>
              <div className="inline-flex flex-col gap-0.5 rounded bg-moss-grey-100 px-2.5 py-1.5 text-12 text-moss-grey-550">
                <span className="font-semibold text-moss-black">
                  {isYearly ? resultsLabels.perYear : resultsLabels.perMonth}
                </span>
                <span>
                  {formatHours(animatedHours)} &middot; {resultsLabels.workDays}
                </span>
              </div>
            </div>
            <div className="flex min-w-0 flex-col gap-3 md:gap-4">
              <p className="min-h-[2.5em] text-14 leading-tight text-moss-grey-550 md:min-h-0 md:text-16 md:leading-none">
                {moneyLabel}
              </p>
              <p className="whitespace-nowrap text-[40px] font-medium leading-none text-moss-black md:text-[64px]">
                <span aria-hidden="true" className="md:hidden">
                  {formatCompactCurrency(animatedMoney, locale, currency, MOBILE_COMPACT_THRESHOLD)}
                </span>
                <span aria-hidden="true" className="hidden md:inline">
                  {formatCompactCurrency(animatedMoney, locale, currency)}
                </span>
                <span className="sr-only">{formatCurrency(totalMoney, locale, currency)}</span>
              </p>
              <div className="inline-flex flex-col gap-0.5 rounded bg-moss-grey-100 px-2.5 py-1.5 text-12 text-moss-grey-550">
                <span className="font-semibold text-moss-black">
                  {isYearly ? resultsLabels.perYear : resultsLabels.perMonth}
                </span>
                <span>
                  {formatHours(animatedHours)} &times; {formatCurrency(hourlyRate, locale, currency)}
                  {resultsLabels.perHourShort} ={' '}
                  <span className="md:hidden">
                    {formatCompactCurrency(totalMoney, locale, currency, MOBILE_COMPACT_THRESHOLD)}
                  </span>
                  <span className="hidden md:inline">{formatCurrency(totalMoney, locale, currency)}</span>
                </span>
              </div>
            </div>
          </div>
        )}
        <ul className={cn('flex flex-col gap-6', !isDrawer && 'border-t border-moss-black/10 pt-6')}>
          {VOLUME_KEYS.map((volumeKey) => (
            <li
              key={volumeKey}
              className="grid grid-cols-[120px_1fr_auto] items-center gap-x-3 text-16 leading-none text-moss-black md:grid-cols-[130px_1fr_auto]"
            >
              <span>{breakdownLabels[volumeKey]}</span>
              <div aria-hidden="true" className="h-4">
                <div
                  className="h-full transition-[width] duration-700 ease-in-out"
                  style={{width: `${widths[volumeKey]}%`}}
                >
                  <div
                    data-testid="breakdown-bar"
                    className={cn(
                      'h-full origin-left animate-roi-bar-grow rounded-sm motion-reduce:animate-none',
                      BAR_CLASS_NAMES[volumeKey],
                    )}
                  />
                </div>
              </div>
              <AnimatedHours hours={savings.hours[volumeKey]} formatHours={formatHours} />
            </li>
          ))}
        </ul>
        {children && (
          <div data-capture-exclude className="flex flex-col gap-3 border-t border-moss-black/10 pt-6">
            {children}
          </div>
        )}
      </div>
    </div>
  )
}
